import type { Theme } from "@mui/material";
import type { EntriliaProduct } from "@entrilia/shared/api/types";
import type { ListItemData } from "@entrilia/shared/components/inputs/CheckItemsList";
import { makeTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import type {
  AccessRole,
  ClientUserType,
  Company,
  MemberRole,
  OrganizationContactRole,
  OrganizationMember,
  OrganizationRole,
} from "../../../../api/types/userManagementTypes";
import { allOrganizationRoles } from "../../../../api/types/userManagementTypes";

export type CompanyAccessLevel = "Product" | "Entity";

export interface CompanyWithRoles {
  clientCode: string;
  roles: MemberRole[];
}

export interface CompanyWithAccess extends CompanyWithRoles {
  entityIds: string[];
  accessToAllEntities: boolean;
}

export const getEntityLevelAccessClientCodes = (
  selectedCompanies: CompanyWithRoles[],
  member: OrganizationMember | undefined,
  companies: Company[]
): string[] => {
  const clientCodes = [
    ...selectedCompanies.map((company) => company.clientCode),
    ...(member?.clientsAccess.map((access) => access.clientCode) ?? []),
  ];
  return [...new Set(clientCodes)]
    .filter((clientCode) => companies.some((company) => company.clientCode === clientCode && company.entityLevelAccess))
    .sort();
};

export interface CompanyProductWithRoles {
  key: EntriliaProduct;
  roles: MemberRole[];
}

export const getAllCompanyProductsWithRoles = (): CompanyProductWithRoles[] => [
  {
    key: "FundAccounting",
    roles: ["FundOps_Viewer", "FundOps_Member", "FundOps_Admin", "FundOps_IR_Admin"],
  },
  {
    key: "CorporateAccounting",
    roles: ["CorpAccounting_Viewer", "CorpAccounting_Admin"],
  },
  {
    key: "InvestorRelationsManagement",
    roles: ["AP_Viewer", "AP_ExternalUser", "AP_Admin"],
  },
  {
    key: "ReportingPortal",
    roles: ["RP_Viewer", "RP_Maker", "RP_Admin"],
  },
  {
    key: "Dashboard",
    roles: ["Dashboard_Viewer", "Dashboard_Admin"],
  },
  {
    key: "ExpenseManagement",
    roles: ["ExpenseMng_Member", "ExpenseMng_Admin"],
  },
  {
    key: "PortfolioMonitoring",
    roles: ["Portfolio_Monitoring_Member", "Portfolio_Monitoring_Submitter", "Portfolio_Monitoring_Admin"],
  },
  {
    key: "CompanyManagement",
    roles: ["Company_Settings_Viewer", "Company_Settings_Admin"],
  },
];

const getCompanyProductsWithRolesForOrgRole = (orgRole: OrganizationRole): CompanyProductWithRoles[] => {
  if (orgRole === "Org_Owner") {
    return getAllCompanyProductsWithRoles();
  }

  if (orgRole === "Org_Member") {
    return getAllCompanyProductsWithRoles();
  }

  if (orgRole === "Org_ExternalUser") {
    return [
      {
        key: "FundAccounting",
        roles: ["FundOps_Viewer", "FundOps_Member"],
      },
      {
        key: "CorporateAccounting",
        roles: ["CorpAccounting_Viewer", "CorpAccounting_Admin"],
      },
      {
        key: "InvestorRelationsManagement",
        roles: ["AP_Viewer", "AP_ExternalUser"],
      },
      {
        key: "ReportingPortal",
        roles: ["RP_Viewer", "RP_Maker"],
      },
      {
        key: "Dashboard",
        roles: ["Dashboard_Viewer"],
      },
      {
        key: "ExpenseManagement",
        roles: ["ExpenseMng_Member"],
      },
      {
        key: "PortfolioMonitoring",
        roles: ["Portfolio_Monitoring_Member", "Portfolio_Monitoring_Submitter", "Portfolio_Monitoring_Admin"],
      },
    ];
  }

  return [];
};

export const getAvailableProductsWithRolesForCompany = (
  organizationRole: OrganizationRole,
  clientCompany: Company | undefined
): CompanyProductWithRoles[] => {
  const allProducts = getCompanyProductsWithRolesForOrgRole(organizationRole);

  const unavailableProducts = new Set<EntriliaProduct>();

  if (!clientCompany?.availableApplications.includes("InvestorRelations")) {
    unavailableProducts.add("InvestorRelationsManagement");
  }
  if (!clientCompany?.availableApplications.includes("ExpenseManagement")) {
    unavailableProducts.add("ExpenseManagement");
  }
  if (!clientCompany?.availableApplications.includes("PortfolioMonitoring")) {
    unavailableProducts.add("PortfolioMonitoring");
  }
  if (!clientCompany?.accountingSettings?.fundAccountingEnabled) {
    unavailableProducts.add("FundAccounting");
  }
  if (!clientCompany?.accountingSettings?.corporateAccountingEnabled) {
    unavailableProducts.add("CorporateAccounting");
  }

  return allProducts.filter((product) => !unavailableProducts.has(product.key));
};

export const filterRolesForAvailableProducts = (
  roles: MemberRole[],
  organizationRole: OrganizationRole,
  clientCompany: Company | undefined
): MemberRole[] => {
  const allowedRoles = new Set(
    getAvailableProductsWithRolesForCompany(organizationRole, clientCompany).flatMap((p) => p.roles)
  );

  return roles.filter((role) => allowedRoles.has(role));
};

export const alignCompaniesAccessToAvailableProducts = (
  organizationRole: OrganizationRole,
  companiesWithAccess: CompanyWithAccess[],
  availableCompanies: Company[]
): CompanyWithAccess[] => {
  return companiesWithAccess.map((companyWithAccess) => {
    const clientCompany = availableCompanies.find((company) => company.clientCode === companyWithAccess.clientCode);
    return {
      ...companyWithAccess,
      roles: filterRolesForAvailableProducts(companyWithAccess.roles, organizationRole, clientCompany),
    };
  });
};

export const roleNamesMap: Record<MemberRole, string> = {
  AP_Viewer: "Viewer",
  AP_Admin: "Admin",
  AP_ExternalUser: "External User",
  RP_Viewer: "Viewer",
  RP_Admin: "Admin",
  RP_Maker: "Maker",
  FundOps_Member: "User",
  FundOps_Admin: "Admin",
  FundOps_Viewer: "Viewer",
  FundOps_IR_Admin: "IR Admin",
  CorpAccounting_Viewer: "Viewer",
  CorpAccounting_Admin: "Admin",
  Company_Settings_Viewer: "Viewer",
  Company_Settings_Admin: "Admin",
  ExpenseMng_Member: "Viewer",
  ExpenseMng_Admin: "Admin",
  Portfolio_Monitoring_Member: "Viewer",
  Portfolio_Monitoring_Submitter: "Submitter",
  Portfolio_Monitoring_Admin: "Admin",
  Dashboard_Viewer: "Viewer",
  Dashboard_Admin: "Admin",
};

type OrganizationRoleData = {
  label: string;
  description: string;
};

export interface MemberFilters {
  search: string;
  types: ClientUserType[];
  role?: OrganizationRole;
  companies: ListItemData[];
}

export const organizationRolesDataMap: Record<OrganizationRole, OrganizationRoleData> = {
  Org_Owner: {
    label: "Owner",
    description: "Highest permission level. Can manage companies, users, billing.",
  },
  Org_Member: {
    label: "Member",
    description: "Regular permission level, restricted by companies given access to.",
  },
  Org_ExternalUser: {
    label: "External",
    description: "An external member with a different email domain than the organization.",
  },
};

export const filterOrganizationRoles = (roles: AccessRole[]): OrganizationRole[] =>
  roles.filter((role) => allOrganizationRoles.includes(role as OrganizationRole)) as OrganizationRole[];

export const getSingleMembersOfOrgContactsRoles = (
  members: OrganizationMember[]
): Map<string, OrganizationContactRole[]> => {
  const userIdToRolesMap = new Map<string, OrganizationContactRole[]>();

  const rolesToUserIdsMap = members.reduce((map, member) => {
    for (const role of member.organizationContactRoles) {
      const userIds = map.get(role) ?? [];
      userIds.push(member.userId);
      map.set(role, userIds);
    }

    return map;
  }, new Map<OrganizationContactRole, string[]>());

  for (const [role, userIds] of rolesToUserIdsMap.entries()) {
    if (userIds.length === 1) {
      const userId = defined(userIds[0]);
      const roles = userIdToRolesMap.get(userId) ?? [];
      roles.push(role);
      userIdToRolesMap.set(userId, roles);
    }
  }

  return userIdToRolesMap;
};

export const formatClientUserType = (userType: ClientUserType) => {
  switch (userType) {
    case "Internal":
      return "Internal";
    case "External":
      return "External";
    case "Support":
      return "Entrilia";
    default:
      return userType;
  }
};

export const getSelectedSx = (theme: Theme, isSelected?: boolean) =>
  isSelected
    ? {
        backgroundColor: makeTintedBackgroundFromColor(theme, theme.palette.primary.main),
        fieldset: {
          color: theme.palette.primary.main,
          borderWidth: "2px",
          borderColor: theme.palette.primary.main,
        },
      }
    : {};

export const getDefaultGridSx = (theme: Theme) => {
  return {
    ".member-row": {
      color: theme.palette.text.primary,
    },
    ".support-member-row": {
      color: theme.palette.success.main,
    },
    ".current-member-row": {
      ".MuiCheckbox-root .MuiSvgIcon-root": {
        color: theme.palette.info.main,
      },
    },
  };
};

export type MembersTab = "active" | "deactivated" | "pending";
